"use client";

import React, { useMemo, useRef, useState, useId } from "react";
import { UploadCloud, FileText, Trash2 } from "lucide-react";
import "./DocDropzone.css";

export type DocDropzoneProps = {
  file?: File | null;                     // controlled file
  defaultFileName?: string;               // server-side file name
  defaultFileSizeBytes?: number | null;   // server-side file size (optional)
  defaultFileUrl?: string;                // server-side file url (optional)
  onRemoveExisting?: () => void;          // callback on removing server file

  onFileChange?: (file: File | null) => void;

  required?: boolean;
  accept?: string | string[];             // e.g. ["image/*",".png",".jpg"] or "application/pdf"
  maxSizeMb?: number;
  disabled?: boolean;

  hint?: string;
  dropText?: string;
  clickText?: string;
  browseText?: string;
  ariaLabel?: string;
  size?: "sm" | "md" | "lg";
  showRemove?: boolean;

  onValidateError?: (message: string) => void;
  strictPdfMagic?: boolean;               // only applies when file is actually a PDF
  className?: string;
  style?: React.CSSProperties;
};

// --- helpers ---
const bytesToSize = (bytes: number) => {
  if (!bytes) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const normalizeAccept = (accept?: string | string[]) => {
  // Default: allow any (no client-side type restriction)
  if (!accept) return [] as string[];
  return (Array.isArray(accept) ? accept : accept.split(","))
    .map((s) => s.trim())
    .filter(Boolean);
};

// supports:
// - exact mime: "image/png"
// - wildcard mime: "image/*"
// - extension: ".png"
const fileMatchesAccept = (file: File, acceptList: string[]) => {
  if (acceptList.length === 0) return true; // no restriction
  const type = (file.type || "").toLowerCase();            // e.g. "image/png"
  const [tMajor, tMinor] = type.split("/");
  const ext = (file.name || "").toLowerCase().split(".").pop() || "";

  return acceptList.some((ruleRaw) => {
    const rule = ruleRaw.toLowerCase();

    // extension rule (".png", ".jpg", ".pdf", ...)
    if (rule.startsWith(".")) return ext === rule.slice(1);

    // wildcard mime ("image/*")
    if (rule.endsWith("/*")) {
      const major = rule.slice(0, -2); // "image"
      return tMajor === major;
    }

    // exact mime ("image/png", "application/pdf")
    return type === rule;
  });
};

const isSafeFileName = (file: File) => {
  const name = file.name || "";
  if (name.length > 200) return false;                // too long
  if (/[\x00-\x1F]/.test(name)) return false;         // control chars
  if (/[\/\\]/.test(name)) return false;              // path separators
  return true;
};

const isPdfLike = (file: File) => {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase().trim();
  return type === "application/pdf" || (name.endsWith(".pdf") && !name.endsWith(".pdf.exe"));
};

const isPdfMagicBytes = async (file: File) => {
  try {
    const head = await file.slice(0, 5).arrayBuffer();
    const sig = new TextDecoder().decode(new Uint8Array(head));
    return sig === "%PDF-";
  } catch {
    return false;
  }
};

const DocDropzone: React.FC<DocDropzoneProps> = ({
  file: controlledFile,
  defaultFileName,
  defaultFileSizeBytes,
  defaultFileUrl,
  onRemoveExisting,
  onFileChange,
  required = false,
  accept,
  maxSizeMb,
  disabled = false,
  hint,
  dropText = "Drop your file here",
  clickText = "or click to upload",
  browseText = "Browse",
  ariaLabel = "Document upload area",
  size = "md",
  showRemove = true,
  onValidateError,
  strictPdfMagic = true,
  className,
  style,
}) => {
  // controlled/uncontrolled bridge
  const [innerFile, setInnerFile] = useState<File | null>(null);
  const activeFile = typeof controlledFile !== "undefined" ? controlledFile : innerFile;

  // local ui
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const acceptList = useMemo(() => normalizeAccept(accept), [accept]);
  const inputId = useId();

  // async validator
  const validate = async (f: File) => {
    if (!isSafeFileName(f)) return "Unsafe file name.";

    // Respect `accept` rather than hard-coding PDF
    if (!fileMatchesAccept(f, acceptList)) {
      return acceptList.length
        ? `Only ${acceptList.join(", ")} file(s) are allowed.`
        : "Unsupported file type.";
    }

    // Size check (if provided)
    if (maxSizeMb && f.size > maxSizeMb * 1024 * 1024) {
      return `File must be smaller than ${maxSizeMb} MB.`;
    }

    // PDF magic check only when PDF is actually accepted and the file is PDF
    const acceptsPdf =
      acceptList.length === 0 ||            // no restriction -> may include pdf
      acceptList.includes("application/pdf") ||
      acceptList.includes(".pdf");
    if (strictPdfMagic && acceptsPdf && isPdfLike(f)) {
      const ok = await isPdfMagicBytes(f);
      if (!ok) return "Corrupted or non-PDF file (failed signature check).";
    }

    return null;
  };

  const setFile = (f: File | null) => {
    onFileChange?.(f);
    if (typeof controlledFile === "undefined") setInnerFile(f);
  };

  const handlePick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleFile = async (f: File | null) => {
    if (!f) {
      setError(null);
      setFile(null);
      return;
    }
    const v = await validate(f);
    if (v) {
      setError(v);
      onValidateError?.(v);
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  };

  // drag & drop
  const onDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = async (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0] || null;
    await handleFile(f);
  };

  const containerClass = [
    "docdz",
    `docdz--${size}`,
    dragOver ? "docdz--dragover" : "",
    error ? "docdz--error" : "",
    disabled ? "docdz--disabled" : "",
    className || "",
  ].filter(Boolean).join(" ");

  // Build a friendly hint from accept list if no explicit hint provided
  const computedHint =
    hint ??
    (acceptList.length
      ? `Allowed: ${acceptList.join(", ")}.`
      : "Allowed: any file type.");

  return (
    <div className="docdz-wrapper" style={style}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={acceptList.join(",")}
        onChange={(e) => {
          // capture and reset before any await (avoid stale event)
          const input = e.currentTarget;
          const f = input.files?.[0] ?? null;
          input.value = "";
          void handleFile(f);
        }}
        style={{ display: "none" }}
        disabled={disabled}
      />

      <div
        className={containerClass}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={handlePick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handlePick()}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        aria-describedby="docdz-hint"
      >
        <div className="docdz-icon">
          <UploadCloud className="docdz-icon-svg" />
        </div>
        <div className="docdz-cta">
          <strong>{dropText}</strong>
          <span>
            {clickText}{" "}
            <button
              type="button"
              className="docdz-browse"
              onClick={(e) => {
                e.stopPropagation();
                handlePick();
              }}
              disabled={disabled}
            >
              {browseText}
            </button>
          </span>
        </div>
        {required && <span className="docdz-required">Required</span>}
      </div>

      <div className="docdz-foot">
        {activeFile ? (
          // selected new file
          <div className="docdz-file">
            <FileText className="docdz-file-icon" />
            <span className="docdz-file-name" title={activeFile.name}>{activeFile.name}</span>
            <span className="docdz-file-size">• {bytesToSize(activeFile.size)}</span>
            {showRemove && !disabled && (
              <button
                type="button"
                className="docdz-remove"
                onClick={() => handleFile(null)}
                aria-label="Remove file"
                title="Remove file"
              >
                <Trash2 className="docdz-remove-icon" />
                Remove
              </button>
            )}
          </div>
        ) : defaultFileName ? (
          // server-side file (no File object)
          <div className="docdz-file">
            <FileText className="docdz-file-icon" />
            <span className="docdz-file-name" title={defaultFileName}>
              {defaultFileUrl ? (
                <a href={defaultFileUrl} target="_blank" rel="noopener noreferrer">{defaultFileName}</a>
              ) : defaultFileName}
            </span>
            {typeof defaultFileSizeBytes === "number" && (
              <span className="docdz-file-size">• {bytesToSize(defaultFileSizeBytes)}</span>
            )}
            {showRemove && !disabled && (
              <button
                type="button"
                className="docdz-remove"
                onClick={() => {
                  onRemoveExisting?.();         // ask parent to mark removal
                  onFileChange?.(null);         // sync uncontrolled/parent state
                }}
                aria-label="Remove file"
                title="Remove file"
              >
                <Trash2 className="docdz-remove-icon" />
                Remove
              </button>
            )}
          </div>
        ) : (
          <small id="docdz-hint" className="ms-hint">
            {computedHint}
          </small>
        )}
        {error && <small className="docdz-error">{error}</small>}
      </div>
    </div>
  );
};

export default DocDropzone;
